import { useCallback, useState } from 'react';
import { useModal } from 'react-modal-hook';

import ConfirmationModal from '@/components/ConfirmationModalV2';
import LoadingState from '@/components/LoadingState';
import { Heading, Subheading } from '@/components/Typography';
import { getDateandLabel, getTimeString } from '@/util/date';

import { useBroadcast } from '../api/useBroadcast';
import { useRecallBroadcast } from '../api/useRecallBroadcast';
import type { Broadcast } from '../types';

interface CommsBroadcastDetailProps {
  broadcastId: string;
}

const containerStyle: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  overflow: 'hidden',
};

const scrollAreaStyle: React.CSSProperties = {
  flex: 1,
  overflowY: 'auto',
  padding: '20px 24px 32px',
};

const headerCardStyle: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: 8,
  padding: '16px 18px',
  marginBottom: 18,
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
};

const recallButtonStyle: React.CSSProperties = {
  appearance: 'none',
  border: '1px solid #f87171',
  background: '#fff',
  color: '#b91c1c',
  fontSize: 12,
  fontWeight: 600,
  padding: '6px 12px',
  borderRadius: 6,
  cursor: 'pointer',
};

const recalledTagStyle: React.CSSProperties = {
  display: 'inline-block',
  fontSize: 11,
  fontWeight: 600,
  color: '#b91c1c',
  background: '#fee2e2',
  padding: '2px 8px',
  borderRadius: 4,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
};

const errorStyle: React.CSSProperties = {
  color: '#b91c1c',
  background: '#fee2e2',
  padding: '6px 12px',
  fontSize: 13,
  borderRadius: 6,
};

export function CommsBroadcastDetail({
  broadcastId,
}: CommsBroadcastDetailProps) {
  const { broadcast, loading: broadcastLoading } = useBroadcast(broadcastId);
  const { recall, recalling, error: recallError } = useRecallBroadcast();
  const [recallSuccess, setRecallSuccess] = useState(false);

  const confirmRecall = useCallback(async () => {
    if (!broadcast) return;
    try {
      await recall(broadcast.id);
      setRecallSuccess(true);
    } catch {
      /* error surfaced via recallError */
    }
  }, [broadcast, recall]);

  const [showRecallModal, hideRecallModal] = useModal(
    () => (
      <ConfirmationModal
        title="Recall this broadcast?"
        message="Workers will see this broadcast as recalled."
        subMessage="They will no longer be able to view the content. This cannot be undone."
        confirmLabel="Recall"
        cancelLabel="Cancel"
        onConfirm={(e) => {
          e.stopPropagation();
          hideRecallModal();
          void confirmRecall();
        }}
        onCancel={(e) => {
          e.stopPropagation();
          hideRecallModal();
        }}
      />
    ),
    [confirmRecall],
  );

  const handleRecall = useCallback(() => {
    if (!broadcast || broadcast.recalled_at) return;
    showRecallModal();
  }, [broadcast, showRecallModal]);

  if (broadcastLoading && !broadcast) {
    return (
      <div style={containerStyle}>
        <LoadingState />
      </div>
    );
  }

  if (!broadcast) {
    return (
      <div style={containerStyle}>
        <div style={{ padding: 24 }}>
          <Subheading color="lighter">Broadcast not found.</Subheading>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={scrollAreaStyle}>
        <BroadcastHeaderCard
          broadcast={broadcast}
          onRecall={handleRecall}
          recalling={recalling}
          recallSuccess={recallSuccess}
          recallError={recallError}
        />
      </div>
    </div>
  );
}

interface BroadcastHeaderCardProps {
  broadcast: Broadcast;
  onRecall: () => void;
  recalling: boolean;
  recallSuccess: boolean;
  recallError: string | null;
}

function BroadcastHeaderCard({
  broadcast,
  onRecall,
  recalling,
  recallSuccess,
  recallError,
}: BroadcastHeaderCardProps) {
  const recalled = !!broadcast.recalled_at || recallSuccess;
  return (
    <div style={headerCardStyle}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <Heading>📣 {broadcast.title || 'Broadcast'}</Heading>
        {recalled ? (
          <span style={recalledTagStyle}>Recalled</span>
        ) : (
          <button
            type="button"
            disabled={recalling}
            onClick={onRecall}
            style={{
              ...recallButtonStyle,
              opacity: recalling ? 0.6 : 1,
              cursor: recalling ? 'wait' : 'pointer',
            }}
          >
            {recalling ? 'Recalling…' : 'Recall'}
          </button>
        )}
      </div>
      <div style={{ fontSize: 14, color: '#111827', whiteSpace: 'pre-wrap' }}>
        {broadcast.content}
      </div>
      <div
        style={{
          fontSize: 12,
          color: '#6b7280',
          display: 'flex',
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <span>{audienceLabel(broadcast)}</span>
        <span>·</span>
        <span>Sent {fullDateLabel(broadcast.created_at)}</span>
        {broadcast.expires_at ? (
          <>
            <span>·</span>
            <span>Expires {fullDateLabel(broadcast.expires_at)}</span>
          </>
        ) : null}
      </div>
      {recallError ? <div style={errorStyle}>{recallError}</div> : null}
    </div>
  );
}

function audienceLabel(broadcast: Broadcast): string {
  const f = broadcast.audience_filter;
  if (!f) return 'Workers';
  if (f.mode === 'direct') {
    const count = f.user_ids?.length ?? 0;
    return count === 1 ? '1 worker' : `${count} workers`;
  }
  const parts: string[] = [];
  const skillsCount = f.skills?.length ?? 0;
  if (skillsCount > 0) {
    parts.push(`${skillsCount} skill${skillsCount === 1 ? '' : 's'}`);
  }
  if (f.tax_types?.length) parts.push(f.tax_types.join('+').toUpperCase());
  if (f.location)
    parts.push(`${f.location.radius_miles}mi · ${f.location.zip_code}`);
  return parts.length > 0 ? parts.join(' · ') : 'All workers';
}

function fullDateLabel(iso: string): string {
  const d = new Date(iso);
  return `${getDateandLabel(d)} · ${getTimeString(d)}`;
}
